/**
 * Server state and URL state for the clinic page (docs/ux/clinic.md): the
 * week window and one owner of `?view`, `?week`, `?clinic`, `?doctor` and
 * the phone's `?day`; the cases awaiting a booking; and the two
 * moves a nurse makes from the page (book, attended). A move reads the
 * matter first, as the hub does, so the version travels with the request
 * and the engine's guard decides (OPEN 80, provisional).
 */

import { defaultOrder } from '../pipeline/views';
import { eventLabel } from '@beam/core/status';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ApiError, api, reasonOf } from '@/app/api';
import type { ClinicRow, ClinicWindow, MatterDetail, MatterRow, TransitionResponse } from '@/app/types';
import { CONFLICT_TEXT, detailKey, movedText, refreshAudit } from '@/features/matter/useMatter';
import type { BookingValues } from '@/features/matter/BookingForm';
import { addDays, parseWeek, weekKey } from './describe';

export type ClinicView = 'list' | 'week';
export type ClinicFilters = { view: ClinicView; week: Date; clinic: string | null; doctor: string | null; day: string | null };

export function parseClinicFilters(params: URLSearchParams): ClinicFilters {
  return {
    view: params.get('view') === 'week' ? 'week' : 'list',
    week: parseWeek(params.get('week')),
    clinic: params.get('clinic') || null,
    doctor: params.get('doctor') || null,
    day: params.get('day') || null,
  };
}

export function serialiseClinicFilters(f: ClinicFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.view === 'week') p.set('view', 'week');
  if (weekKey(f.week) !== weekKey(parseWeek(null))) p.set('week', weekKey(f.week));
  if (f.clinic) p.set('clinic', f.clinic);
  if (f.doctor) p.set('doctor', f.doctor);
  if (f.day) p.set('day', f.day);
  return p;
}

export function useClinicFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseClinicFilters(params), [params]);
  const update = useCallback((patch: Partial<ClinicFilters>) => {
    setParams((prev) => serialiseClinicFilters({ ...parseClinicFilters(prev), ...patch }), { replace: true });
  }, [setParams]);
  return { filters, update };
}

export const clinicKey = (from: string, to: string) => ['clinic', from, to] as const;
export const awaitingBookingKey = ['clinic', 'awaiting-booking'] as const;

export function useClinicWeek(monday: Date) {
  const from = weekKey(monday);
  const to = weekKey(addDays(monday, 7));
  return useQuery({
    queryKey: clinicKey(from, to),
    queryFn: () => api<ClinicWindow>(`/api/clinic/appointments?from=${from}&to=${to}`),
    staleTime: 10_000,
    retry: (count) => count < 1,
  });
}

/** Matters at 4 Paperwork Sorted that need an appointment: a file review needs none (the engine skips 5), so it is not listed. */
export function awaitingBooking(rows: MatterRow[]): MatterRow[] {
  return rows.filter((r) => r.status === 4 && r.reportType !== 'file_review').toSorted((a, b) => defaultOrder(a, b) || a.ref.localeCompare(b.ref));
}

export function useAwaitingBooking() {
  return useQuery({
    queryKey: awaitingBookingKey,
    queryFn: () => api<MatterRow[]>('/api/matters'),
    select: awaitingBooking,
    staleTime: 10_000,
    retry: (count) => count < 1,
  });
}

export function applyClinicFilters(rows: ClinicRow[], f: ClinicFilters): ClinicRow[] {
  return rows.filter((r) => (!f.clinic || r.clinic.id === f.clinic) && (!f.doctor || r.doctor.slug === f.doctor));
}

export const activeClinicFilters = (f: ClinicFilters): number => (f.clinic ? 1 : 0) + (f.doctor ? 1 : 0);

export type ClinicMove = { ref: string; event: 'appointment_booked' | 'attended'; appointment?: BookingValues };

/** The engine's option for the move was not allowed when the case was read: the reason is the service's, shown as the hub shows a refusal. */
class NotAllowed extends Error {
  // The field is declared and assigned rather than written as a constructor parameter property,
  // which `erasableSyntaxOnly` refuses in the web app's TypeScript configuration.
  readonly reason: string;
  constructor(reason: string) {
    super(reason);
    this.reason = reason;
  }
}

export function useClinicMove(callbacks: { onConflict?: () => void; onMoved?: (r: TransitionResponse) => void } = {}) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (move: ClinicMove) => {
      const path = `/api/matters/${encodeURIComponent(move.ref)}`;
      const detail = await api<MatterDetail>(path);
      const option = detail.options.find((o) => o.kind === move.event);
      if (!option) throw new NotAllowed(`${eventLabel(move.event)} is not offered at ${detail.matter.status}`);
      if (!option.allowed) throw new NotAllowed(option.reason ?? `${eventLabel(move.event)} is not allowed`);
      return api<TransitionResponse>(`${path}/transitions`, { method: 'POST', json: { event: move.event, expectedVersion: detail.matter.version, ...(move.appointment ? { appointment: move.appointment } : {}) } });
    },
    onSuccess: (data, move) => {
      const { moved, ...detail } = data;
      client.setQueryData(detailKey(move.ref), detail);
      void refreshAudit(client, move.ref);
      void client.invalidateQueries({ queryKey: ['clinic'] });
      void client.invalidateQueries({ queryKey: ['matters'] });
      void client.invalidateQueries({ queryKey: ['pipeline'] });
      toast.success(`${move.ref}: ${movedText(moved.to, moved.flags, moved.itemsAdded, moved.from)}`);
      callbacks.onMoved?.(data);
    },
    onError: (err, move) => {
      void client.invalidateQueries({ queryKey: ['clinic'] });
      if (err instanceof ApiError && err.status === 409) {
        void client.invalidateQueries({ queryKey: detailKey(move.ref) });
        toast.error(CONFLICT_TEXT);
        callbacks.onConflict?.();
        return;
      }
      if (err instanceof NotAllowed || (err instanceof ApiError && err.status === 422)) {
        const reason = err instanceof NotAllowed ? err.reason : reasonOf(err) ?? 'the engine refused';
        toast.error(`Could not move ${move.ref}: ${reason}. Nothing changed.`);
        return;
      }
      // A retry re-reads the matter, so a stale version is never re-sent.
      toast.error(`Could not move ${move.ref}. Nothing changed.`, { action: { label: 'Retry', onClick: () => mutation.mutate(move) } });
    },
  });
  return mutation;
}
